import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress
} from "@mui/material";
import { useState, useEffect } from "react";
import { RTL } from "../../cache/CacheRtl";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {
    createTask,
    updateTask,
    TASK_PRIORITIES,
    PRIORITY_OPTIONS,
    CATEGORY_OPTIONS
} from "../../services/TaskService";
import { getClients } from "../../services/TraineeService";
import { useMessageContext } from "../../contexts/MessageContext";
import { useAuth } from "../../contexts/AuthContext";
import { useCompany } from "../../contexts/CompanyContext";
import { t } from "../dashboard/coach/coachTokens";

const TaskDialog = ({ isOpen, close, setReload, task = null, suggestionData = null }) => {
    const { notificationHandler } = useMessageContext();
    const { currentUser, isTrainer, isCompanyAdmin, isSuperAdmin } = useAuth();
    const { company } = useCompany();

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        priority: TASK_PRIORITIES.MEDIUM,
        category: null,
        dueDate: null,
        reminderDate: null,
        traineeId: null
    });

    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingClients, setLoadingClients] = useState(false);
    const [errors, setErrors] = useState({});

    const isEdit = task !== null;

    useEffect(() => {
        if (isOpen) {
            loadClients();
            if (isEdit) {
                populateFormForEdit();
            } else if (suggestionData) {
                populateFromSuggestion();
            } else {
                resetForm();
            }
        }
    }, [isOpen, task, suggestionData]);

    const loadClients = async () => {
        setLoadingClients(true);
        try {
            // Get trainer ID for filtering
            let trainerId = null;
            if (isTrainer() && !isCompanyAdmin() && !isSuperAdmin()) {
                trainerId = currentUser?.id;
            }

            const response = await getClients(0, 100, null, trainerId, null, null, company?.id);
            setClients(response.data?.content || []);
        } catch (error) {
            console.error("Error loading clients:", error);
        } finally {
            setLoadingClients(false);
        }
    };

    const populateFormForEdit = () => {
        setFormData({
            title: task.title || "",
            description: task.description || "",
            priority: task.priority || TASK_PRIORITIES.MEDIUM,
            category: task.category || null,
            dueDate: task.dueDate ? dayjs(task.dueDate * 1000) : null,
            reminderDate: task.reminderDate ? dayjs(task.reminderDate * 1000) : null,
            traineeId: task.traineeId || null
        });

        if (task.traineeId && task.traineeName) {
            setSelectedClient({ id: task.traineeId, name: task.traineeName });
        } else {
            setSelectedClient(null);
        }
    };

    const populateFromSuggestion = () => {
        setFormData({
            title: suggestionData.title || "",
            description: suggestionData.description || "",
            priority: suggestionData.priority || TASK_PRIORITIES.MEDIUM,
            category: suggestionData.category || null,
            dueDate: null,
            reminderDate: null,
            traineeId: suggestionData.traineeId || null
        });

        if (suggestionData.traineeId && suggestionData.traineeName) {
            setSelectedClient({ id: suggestionData.traineeId, name: suggestionData.traineeName });
        } else {
            setSelectedClient(null);
        }
    };

    const resetForm = () => {
        setFormData({
            title: "",
            description: "",
            priority: TASK_PRIORITIES.MEDIUM,
            category: null,
            dueDate: null,
            reminderDate: null,
            traineeId: null
        });
        setSelectedClient(null);
        setErrors({});
    };

    const handleChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleClientChange = (event, newValue) => {
        setSelectedClient(newValue);
        setFormData(prev => ({
            ...prev,
            traineeId: newValue?.id || null
        }));
    };

    const handleDateChange = (field) => (newValue) => {
        setFormData(prev => ({
            ...prev,
            [field]: newValue
        }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) {
            newErrors.title = "שדה חובה";
        }
        if (formData.title.length > 500) {
            newErrors.title = "הכותרת ארוכה מדי (מקסימום 500 תווים)";
        }
        if (formData.description && formData.description.length > 2000) {
            newErrors.description = "התיאור ארוך מדי (מקסימום 2000 תווים)";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        try {
            const payload = {
                title: formData.title.trim(),
                description: formData.description?.trim() || null,
                priority: formData.priority,
                category: formData.category,
                dueDate: formData.dueDate ? formData.dueDate.unix() : null,
                reminderDate: formData.reminderDate ? formData.reminderDate.unix() : null,
                traineeId: formData.traineeId
            };

            if (isEdit) {
                payload.id = task.id;
                await updateTask(payload);
                notificationHandler.success("המשימה עודכנה בהצלחה");
            } else {
                await createTask(payload);
                notificationHandler.success("המשימה נוצרה בהצלחה");
            }

            setReload();
            close();
        } catch (error) {
            console.error("Error saving task:", error);
            notificationHandler.error("שגיאה בשמירת המשימה");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        resetForm();
        close();
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: t.r,
                    direction: "rtl"
                }
            }}
        >
            <DialogTitle sx={{
                borderBottom: `1px solid ${t.border}`,
                padding: "20px 24px",
                fontSize: "18px",
                fontWeight: 700,
                color: t.text
            }}>
                {isEdit ? "עריכת משימה" : "משימה חדשה"}
            </DialogTitle>

            <DialogContent sx={{ padding: "24px", paddingTop: "24px !important" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {/* Title */}
                    <RTL>
                        <TextField
                            label="כותרת המשימה"
                            value={formData.title}
                            onChange={handleChange("title")}
                            fullWidth
                            required
                            error={!!errors.title}
                            helperText={errors.title}
                            inputProps={{ maxLength: 500 }}
                        />
                    </RTL>

                    {/* Description */}
                    <RTL>
                        <TextField
                            label="תיאור (אופציונלי)"
                            value={formData.description}
                            onChange={handleChange("description")}
                            fullWidth
                            multiline
                            rows={3}
                            error={!!errors.description}
                            helperText={errors.description}
                            inputProps={{ maxLength: 2000 }}
                        />
                    </RTL>

                    {/* Priority & Category Row */}
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <RTL>
                            <FormControl fullWidth>
                                <InputLabel>עדיפות</InputLabel>
                                <Select
                                    value={formData.priority}
                                    onChange={handleChange("priority")}
                                    label="עדיפות"
                                >
                                    {PRIORITY_OPTIONS.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </RTL>

                        <RTL>
                            <FormControl fullWidth>
                                <InputLabel>קטגוריה</InputLabel>
                                <Select
                                    value={formData.category || ""}
                                    onChange={handleChange("category")}
                                    label="קטגוריה"
                                >
                                    <MenuItem value="">
                                        <em>ללא קטגוריה</em>
                                    </MenuItem>
                                    {CATEGORY_OPTIONS.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </RTL>
                    </Box>

                    {/* Due Date */}
                    <RTL>
                        <DatePicker
                            label="תאריך יעד (אופציונלי)"
                            value={formData.dueDate}
                            onChange={handleDateChange("dueDate")}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    fullWidth: true
                                }
                            }}
                        />
                    </RTL>

                    {/* Client Selection */}
                    <RTL>
                        <Autocomplete
                            options={clients}
                            getOptionLabel={(option) => option.name || ""}
                            value={selectedClient}
                            onChange={handleClientChange}
                            loading={loadingClients}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="לקוח (אופציונלי)"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingClients ? <CircularProgress size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            noOptionsText="לא נמצאו לקוחות"
                        />
                    </RTL>

                    {/* Reminder Date */}
                    <RTL>
                        <DatePicker
                            label="תזכורת (אופציונלי)"
                            value={formData.reminderDate}
                            onChange={handleDateChange("reminderDate")}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    fullWidth: true
                                }
                            }}
                        />
                    </RTL>
                </Box>
            </DialogContent>

            <DialogActions sx={{
                padding: "16px 24px",
                borderTop: `1px solid ${t.border}`,
                gap: 1
            }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        color: t.t2,
                        borderColor: t.border,
                        "&:hover": {
                            borderColor: t.t3,
                            backgroundColor: t.bg
                        }
                    }}
                >
                    ביטול
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                    sx={{
                        backgroundColor: t.green,
                        "&:hover": {
                            backgroundColor: t.greenDark
                        }
                    }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : (isEdit ? "שמור שינויים" : "צור משימה")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaskDialog;
